import { JSX, useEffect, useState } from 'react';
import { isIOS, isMobile } from 'react-device-detect';
import { Trans, useTranslation } from 'react-i18next';
import { FaInfoCircle } from 'react-icons/fa';
import { FiPlusSquare } from 'react-icons/fi';
import { GoShare } from 'react-icons/go';
import { HiDotsVertical } from 'react-icons/hi';
import { MdAddToHomeScreen } from 'react-icons/md';

export function InstallInstructions(): JSX.Element {
  const { t } = useTranslation();

  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const media = '(display-mode: standalone)';

    function checkPWA() {
      setIsInstalled(
        window.matchMedia(media).matches || (window.navigator as Navigator & { standalone: boolean }).standalone,
      );
    }

    checkPWA();

    window.matchMedia(media).addEventListener('change', checkPWA);

    return () => window.matchMedia(media).removeEventListener('change', checkPWA);
  }, []);

  return isMobile && !isInstalled ? (
    <>
      <details className="collapse bg-base-100">
        <summary className="collapse-title p-4 font-bold text-center text-lg">
          <div className="flex items-center justify-center gap-1">
            <FaInfoCircle />
            <div>{t('Install Instructions')}</div>
          </div>
        </summary>
        <div className="collapse-content text-sm">
          <ol className="list-decimal ml-5">
            {isIOS ? (
              <>
                <li>
                  <Trans i18nKey="install.ios.0">
                    Make sure you are using a compatible browser <IosBrowsers />
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.ios.1">
                    Tap the <IosShare /> button
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.ios.2">
                    Find and select <IosAdd />
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.ios.3">
                    Confirm by tapping <span className="font-bold">Add</span>
                  </Trans>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Trans i18nKey="install.android.0">
                    Make sure you are using a compatible browser <AndroidBrowsers />
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.android.1">
                    Tap the <AndroidMenu /> in the upper-right corner of the browser
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.android.2">
                    Select <AndroidAdd /> from the menu
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="install.android.3">
                    Confirm by tapping <span className="font-bold">Install</span>
                  </Trans>
                </li>
              </>
            )}
          </ol>
        </div>
      </details>
    </>
  ) : (
    <></>
  );
}

// nested translation components
function IosBrowsers(): JSX.Element {
  return <span className="text-xs">(Safari)</span>;
}

function IosShare(): JSX.Element {
  const { t } = useTranslation();

  return (
    <span className="font-bold inline-flex items-center">
      {t('Share')} <GoShare className="ml-1" />
    </span>
  );
}

function IosAdd(): JSX.Element {
  const { t } = useTranslation();

  return (
    <span className="font-bold inline-flex items-center">
      {t('Add to Home Screen')} <FiPlusSquare className="ml-1" />
    </span>
  );
}

function AndroidBrowsers(): JSX.Element {
  return <span className="text-xs">(Chrome, Edge, Samsung, Opera)</span>;
}

function AndroidMenu(): JSX.Element {
  const { t } = useTranslation();

  return (
    <span className="font-bold inline-flex items-center">
      {t('three dots menu')} <HiDotsVertical />
    </span>
  );
}

function AndroidAdd(): JSX.Element {
  const { t } = useTranslation();

  return (
    <span className="font-bold inline-flex items-center">
      {t('Add to Home Screen')} <MdAddToHomeScreen />
    </span>
  );
}
